import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { client } from '@/lib/api';
import type { document } from '@/lib/client';
import { planCardOf } from '@/lib/plans';
import type { InteractiveMode } from '@/types';
import { POLL_MS, useDocumentsStore, WORKING } from '@/stores/documentsStore';
import { Zacking } from '@/components/changes/Zacking';
import { DocumentQuestions } from '@/components/changes/DocumentQuestions';

const WORKING_TEXT: Partial<Record<document.DocumentStatus, string>> = {
  decomposing: 'Reading the request: what it asks for and what it leaves open. About a minute.',
  planning: 'Making the plan: each change matched to Zoom\'s API, your account read for the ids. One to three minutes.',
};

/**
 * A request under the reply (docs/plans/chat-on-document-pipeline.md): followed while ZAC reads it, its questions
 * answered here, and once its plan is made the card becomes the plan's (Approve & run, Open plan) through `onPlanned`.
 */
export function DocumentChatCard({ id, onPlanned }: { id: string; onPlanned: (card: InteractiveMode) => void }) {
  const doc = useDocumentsStore((s) => s.byId[id]);
  const load = useDocumentsStore((s) => s.load);
  const answer = useDocumentsStore((s) => s.answer);
  const busy = useDocumentsStore((s) => s.busy.answer ?? false);
  const error = useDocumentsStore((s) => s.errorById[id] ?? null);
  // The caller's callback may change every render (the report chat's); the plan is loaded once per plan.
  const planned = useRef(onPlanned);
  planned.current = onPlanned;

  useEffect(() => {
    void load(id);
  }, [id, load]);

  // A job runs: read the request again until it ends.
  const working = doc !== undefined && WORKING.has(doc.status);
  useEffect(() => {
    if (!working) return;
    const timer = setInterval(() => void load(id), POLL_MS);
    return () => clearInterval(timer);
  }, [working, id, load]);

  // The plan made: the card becomes the plan's. Not for a request a follow-up replaced: its plan is cancelled.
  const planId = doc?.status === 'planned' && doc.superseded_by === null ? doc.plan_id : null;
  useEffect(() => {
    if (planId === null) return;
    let gone = false;
    void client.plans.getPlan(planId).then(({ plan }) => {
      if (!gone) planned.current(planCardOf(plan));
    }).catch(() => {
      // The card stays on the request; its Open button reaches the plan.
    });
    return () => {
      gone = true;
    };
  }, [planId]);

  if (doc === undefined) {
    return <Line><Loader2 className="h-4 w-4 animate-spin" /> Loading the request…</Line>;
  }
  if (doc.superseded_by !== null) {
    return <Line>A follow-up replaced this request; the newer card below carries it on.</Line>;
  }
  return (
    <div className="mb-3 space-y-3">
      {working && <div className="space-y-1.5"><Line>{WORKING_TEXT[doc.status]}</Line><Zacking doc={doc} /></div>}
      {doc.objects.length > 0 && !working && (
        <ul className="rounded-lg border border-border bg-secondary/30 p-3 text-sm">
          {doc.objects.map((o) => (
            <li key={o.key} className="flex gap-2">
              <span className="w-16 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">{o.action}</span>
              <span className="text-foreground">{o.kind} <span className="font-medium">"{o.name}"</span></span>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</p>}
      {doc.status === 'failed' && doc.error && <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{doc.error}</p>}
      {doc.status === 'failed' && doc.round === 0 && (
        <button onClick={() => void answer(doc.id, [])} disabled={busy} className="rounded-lg border border-border px-3 py-2 text-sm text-foreground hover:bg-secondary disabled:opacity-50">
          Read it again
        </button>
      )}
      {doc.status === 'planned' && <Line><Loader2 className="h-4 w-4 animate-spin" /> The plan is made; loading it…</Line>}
      {(doc.status === 'asking' || (doc.status === 'failed' && doc.round > 0)) && <DocumentQuestions key={doc.round} doc={doc} compact />}
    </div>
  );
}

function Line({ children }: { children: React.ReactNode }) {
  return <div className="mb-3 flex items-center gap-2 rounded-lg border border-border bg-secondary/30 px-3 py-2 text-sm text-muted-foreground">{children}</div>;
}
